/**
 * The Mail Settings window (piece 32; T419, decision 1235): one window that
 * gathers the settings the mail client already has, a rail of sections in two
 * groups beside one page per section. 🔨 It moves no value: every section edits
 * the row or the record the piece already declares, through the service that
 * already writes it. 🔒 The server names the sections a person may open, and a
 * section a person may not edit is not drawn for them.
 */
import { MAIL_API_PATH } from './client';

/** The window's own address in the browser, and one section's. */
export const MAIL_SETTINGS_WINDOW_ROUTE = '/mail/settings';
export const mailSettingsSectionRoute = (section: MailSettingsSectionKey): string => `${MAIL_SETTINGS_WINDOW_ROUTE}/${section}`;

/** What the window asks the server before it draws its rail. */
export const MAIL_SETTINGS_WINDOW_PATH = `${MAIL_API_PATH}/settings/window`;

/** The two groups of the rail: what a person keeps for themself, and what an administrator keeps for everybody. */
export const MAIL_SETTINGS_GROUPS = ['Personal', 'Administration'] as const;
export type MailSettingsGroup = (typeof MAIL_SETTINGS_GROUPS)[number];

/**
 * 🔒 The sections in rail order. 🔨 A section for a thing the client does not
 * have (hotkeys, offline, import and export) has no entry here at all.
 */
export const MAIL_SETTINGS_SECTIONS = [
  { key: 'view-options', group: 'Personal', words: ['reading pane', 'reading format', 'plain text', 'read receipt', 'after deleting', 'layout'] },
  { key: 'compose', group: 'Personal', words: ['reply from', 'reply header', 'undo send', 'delay', 'quoted'] },
  { key: 'conversation', group: 'Personal', words: ['conversation view', 'thread'] },
  { key: 'folders', group: 'Personal', words: ['folder', 'inbox', 'unread'] },
  { key: 'out-of-office', group: 'Personal', words: ['out of office', 'away', 'automatic reply', 'vacation', 'leave'] },
  { key: 'signature', group: 'Personal', words: ['signature', 'sign-off', 'quoted', 'from address'] },
  { key: 'send-as', group: 'Personal', words: ['send as', 'delegation', 'delegate', 'grant', 'on behalf'] },
  { key: 'filters', group: 'Personal', words: ['filter', 'rule', 'incoming'] },
  { key: 'mailboxes', group: 'Administration', words: ['mailbox', 'quota', 'reader', 'legal footer', 'client access', 'credential', 'imap', 'module sender', 'company signature', 'undo send'] },
  { key: 'groups', group: 'Administration', words: ['group', 'distribution list', 'member'] },
  { key: 'shared-mailboxes', group: 'Administration', words: ['shared mailbox', 'department mailbox', 'module mailbox', 'role'] },
  { key: 'archive-retention', group: 'Administration', words: ['archive', 'retention', 'purge', 'departed', 'keep mail'] },
  { key: 'recipient-certificates', group: 'Administration', words: ['certificate', 's/mime', 'smime', 'encryption', 'encrypt', 'recipient'] },
  { key: 'spam-quarantine', group: 'Administration', words: ['spam', 'quarantine', 'allowed sender', 'blocked sender', 'domain', 'phishing', 'block list', 'allow list'] },
] as const satisfies readonly { key: string; group: MailSettingsGroup; words: readonly string[] }[];
export type MailSettingsSectionKey = (typeof MAIL_SETTINGS_SECTIONS)[number]['key'];

export function isMailSettingsSection(value: unknown): value is MailSettingsSectionKey {
  return typeof value === 'string' && MAIL_SETTINGS_SECTIONS.some((s) => s.key === value);
}

/** The strings key a section's title is kept under: its address segment in lower camel case, as every key is. */
export const mailSettingsTitleKey = (key: MailSettingsSectionKey): string => `mail.settingsWindow.section.${key.replace(/-([a-z])/g, (_whole, letter: string) => letter.toUpperCase())}`;

/** The group a section stands in. */
export function mailSettingsGroupOf(key: MailSettingsSectionKey): MailSettingsGroup {
  return MAIL_SETTINGS_SECTIONS.find((s) => s.key === key)!.group;
}

/** 🔒 The sections this person may open, in rail order: the server's answer, never the screen's guess. */
export interface MailSettingsWindowAnswer {
  sections: MailSettingsSectionKey[];
}

/** 🔒 The Mailboxes page's client-access card (decision 1237): the credentials issued for a desktop client, read only. */
export const MAIL_CLIENT_ACCESS_PATH = `${MAIL_API_PATH}/settings/client-access`;

export interface ClientCredentialRow {
  id: string;
  /** The login the credential belongs to, by its address. */
  login: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface ClientAccessAnswer {
  /** The organisation's `mail.clientAccessEnabled` default, which a person's own row overrides. */
  enabledByDefault: boolean;
  credentials: ClientCredentialRow[];
}

/**
 * The sections a person's own settings fill: every one of the Personal group,
 * and the Administration group only for a holder of the capability Settings ›
 * Email refuses on (decision 1235).
 */
export function mailSettingsSectionsFor(facts: { administers: boolean }): MailSettingsSectionKey[] {
  return MAIL_SETTINGS_SECTIONS.filter((s) => s.group === 'Personal' || facts.administers).map((s) => s.key);
}

/**
 * The search box over the rail: a section matches when its title or one of its
 * words holds every word typed, in any order and any case. An empty search
 * keeps every section.
 */
export function searchMailSettings(term: string, sections: readonly MailSettingsSectionKey[], titleOf: (key: MailSettingsSectionKey) => string): MailSettingsSectionKey[] {
  const typed = term.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (typed.length === 0) return [...sections];
  return sections.filter((key) => {
    const entry = MAIL_SETTINGS_SECTIONS.find((s) => s.key === key)!;
    const haystack = [titleOf(key), ...entry.words].join(' ').toLowerCase();
    return typed.every((word) => haystack.includes(word));
  });
}
